import { db, FirebaseUser } from '../../../firebase';
import {
  doc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { trackEvent } from '../server/analytics/trackEvent';
import { SeasonStatsToggles, TeamMenuSettings, UserConfig, UserSettings } from '../../types/user';
import { additionalValidTeamMenuProperties, seasonStatsToggleOptions } from '../../static/constants/userSettings';
import { getTeamMenuOptions } from '../../utils/userSettingsUtils';
import { StringToAnyMap } from '../../types/common';


// we want to check if 'colored', 'orderBy' and 'showLeft' hold valid values
const pruneAdditionalProperties = (
  currentMenu: TeamMenuSettings,
  newMenu: TeamMenuSettings,
  validOptions: string[],
): boolean => {

  let shouldPruneUserSettings = false;

  if (currentMenu.colored && !(validOptions.includes(currentMenu.colored))) {
    newMenu.colored = null;
    shouldPruneUserSettings = true;
  }

  if (currentMenu.orderBy && !(validOptions.includes(currentMenu.orderBy))) {
    newMenu.orderBy = null;
    shouldPruneUserSettings = true;
  }

  if (currentMenu.showLeft && !(validOptions.includes(currentMenu.showLeft))) {
    newMenu.showLeft = null;
    shouldPruneUserSettings = true;
  }

  return shouldPruneUserSettings;
};


// get and set userSettings and prune values that are no longer used or accessible
export const getUserSettings = (
  setUserSettings: (userSettings: UserSettings) => void,
  userConfig: UserConfig,
  hasSkillcorner: boolean,
) => {

  const userSettingsDocRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'settings', userConfig.email);

  const unsubscribe = onSnapshot(userSettingsDocRef, (doc) => {
    const userSettingsObject = doc.data();

    // we want to prune old data and delete inaccessible fields from all team menus
    let shouldPruneUserSettings = false;

    const currentTeamMenu = userSettingsObject?.teamMenu ?? {};
    const newTeamMenu: TeamMenuSettings = {};
    const teamMenuOptions = getTeamMenuOptions('teamMenu', userConfig, hasSkillcorner);
    Object.keys(currentTeamMenu).forEach(key => {
      const property = key as keyof TeamMenuSettings;
      if (teamMenuOptions.includes(property) || additionalValidTeamMenuProperties.includes(property)) {
        newTeamMenu[property] = currentTeamMenu[property];
      }
      else shouldPruneUserSettings = true;
    });
    if (pruneAdditionalProperties(currentTeamMenu, newTeamMenu, teamMenuOptions)) {
      shouldPruneUserSettings = true;
    }


    const currentOwnTeamMenu = userSettingsObject?.ownTeamMenu ?? {};
    const newOwnTeamMenu: TeamMenuSettings = {};
    const ownTeamMenuOptions = getTeamMenuOptions('ownTeamMenu', userConfig, hasSkillcorner);
    Object.keys(currentOwnTeamMenu).forEach(key => {
      const property = key as keyof TeamMenuSettings;
      if (ownTeamMenuOptions.includes(property) || additionalValidTeamMenuProperties.includes(property)) {
        newOwnTeamMenu[property] = currentOwnTeamMenu[property];
      }
      else shouldPruneUserSettings = true;
    });
    if (pruneAdditionalProperties(currentOwnTeamMenu, newOwnTeamMenu, ownTeamMenuOptions)) {
      shouldPruneUserSettings = true;
    }
    if (currentOwnTeamMenu.hideUnavailablePlayers && !userConfig.accessConfig.hasAccessToViewTeamRoles) {
      newOwnTeamMenu.hideUnavailablePlayers = false;
      shouldPruneUserSettings = true;
    }


    const currentOwnTeamPlannerMenu = userSettingsObject?.ownTeamPlannerMenu ?? {};
    const newOwnTeamPlannerMenu: TeamMenuSettings = {};
    const ownTeamPlannerMenuOptions = getTeamMenuOptions('ownTeamPlannerMenu', userConfig, hasSkillcorner);
    Object.keys(currentOwnTeamPlannerMenu).forEach(key => {
      const property = key as keyof TeamMenuSettings;
      if (ownTeamPlannerMenuOptions.includes(property) || additionalValidTeamMenuProperties.includes(property)) {
        newOwnTeamPlannerMenu[property] = currentOwnTeamPlannerMenu[property];
      }
      else shouldPruneUserSettings = true;
    });
    if (pruneAdditionalProperties(currentOwnTeamPlannerMenu, newOwnTeamPlannerMenu, ownTeamPlannerMenuOptions)) {
      shouldPruneUserSettings = true;
    }


    const currentAcademyTeamMenu = userSettingsObject?.academyTeamMenu ?? {};
    const newAcademyTeamMenu: TeamMenuSettings = {};
    const academyTeamMenuOptions = getTeamMenuOptions('academyTeamMenu', userConfig, hasSkillcorner);
    Object.keys(currentAcademyTeamMenu).forEach(key => {
      const property = key as keyof TeamMenuSettings;
      if (academyTeamMenuOptions.includes(property) || additionalValidTeamMenuProperties.includes(property)) {
        newAcademyTeamMenu[property] = currentAcademyTeamMenu[property];
      }
      else shouldPruneUserSettings = true;
    });
    if (pruneAdditionalProperties(currentAcademyTeamMenu, newAcademyTeamMenu, academyTeamMenuOptions)) {
      shouldPruneUserSettings = true;
    }


    const currentSeasonStatsToggles = userSettingsObject?.seasonStatsToggles ?? {};
    const newSeasonStatsToggles: SeasonStatsToggles = {};
    Object.keys(currentSeasonStatsToggles).forEach(key => {
      const property = key as keyof SeasonStatsToggles;
      if (seasonStatsToggleOptions.includes(property)) newSeasonStatsToggles[property] = currentSeasonStatsToggles[property];
      else shouldPruneUserSettings = true;
    });


    const userSettings: UserSettings = {
      academyTeamMenu: newAcademyTeamMenu,
      ownTeamMenu: newOwnTeamMenu,
      ownTeamPlannerMenu: newOwnTeamPlannerMenu,
      teamMenu: newTeamMenu,
      seasonStatsToggles: newSeasonStatsToggles,
      ownTeamMatrixConfig: userSettingsObject?.ownTeamMatrixConfig ?? {},
    };

    // if we should prune user settings, we need to update the document
    if (shouldPruneUserSettings) {
      setDoc(userSettingsDocRef, userSettings);
    }

    setUserSettings(userSettings);
  });

  return unsubscribe;
};


// Update teamMenu or ownTeamMenu settings
export const updateTeamMenu = async (
  menu: StringToAnyMap,
  menuType: string,
  userEmail: string,
  club: string,
  currentUser: FirebaseUser
) => {

  const userDocRef = doc(db, TENANTS_COLLECTION_NAME, club, 'settings', userEmail);

  try {
    await setDoc(userDocRef, {
      [menuType]: menu
    }, { merge: true });
    trackEvent('TeamMenuUpdated', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateTeamMenu', errorDetails: { error } }, currentUser, 'system');
  }
};


// update season stats toggles
export const updateSeasonStatsToggles = async (
  newSeasonStatsToggles: Partial<SeasonStatsToggles>,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser
) => {

  const userDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', userEmail);

  try {
    await setDoc(userDocRef, {
      ['seasonStatsToggles']: newSeasonStatsToggles
    }, { merge: true });
    trackEvent('SeasonStatsTogglesUpdated', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateSeasonStatsToggles', errorDetails: { error } }, currentUser, 'system');
  }
};


// update ownTeamMatrixConfig
export const updateOwnTeamMatrixConfig = async (
  newOwnTeamMatrixConfig: StringToAnyMap,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser
) => {

  const userDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'settings', userEmail);

  try {
    await setDoc(userDocRef, {
      ['ownTeamMatrixConfig']: newOwnTeamMatrixConfig
    }, { merge: true });
    trackEvent('OwnTeamMatrixConfigUpdated', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateOwnTeamMatrixConfig', errorDetails: { error } }, currentUser, 'system');
  }
};
